import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, AlertCircle, Clock, Lock, X, Bug, ExternalLink, EyeOff } from 'lucide-react';
import legacyUapiService from '../../services/LegacyUapiService';
import Button from './Button';
import Spinner from './Spinner';
import CopyButton from './CopyButton';
import ConfirmModal from './ConfirmModal';
import UapiTraceSteps, { DIFF_TITLE, DIFF_SUBTITLE, labelsFor, prettyBody } from './UapiTraceSteps';
import DiffModal from './DiffModal';
import './UapiConsole.css';
import { apiErrorMessage } from '../../utils/apiError';

// Send a real request to the Universal API, as a real consumer.
//
// WHY THE PORTAL AND NOT POSTMAN. Everything a call needs is already resolved on this screen and
// nowhere else together: the credential, the token it just minted, the x-hotelid issued for this
// property, and the operations the route tree declares. Reproducing that by hand is four copy-pastes
// and the usual outcome is a 404 caused by a typo in the path rather than by anything real.
//
// IT IS A REAL CALL. Not a simulation and not a server-side proxy — the browser sends it, with the
// bearer token the page holds, to the gateway. The response shown is the response a consumer gets,
// including the redactions applied on the way out, which is the only way to check that a redaction does
// what its author meant.
//
// WRITE METHODS ARE REAL TOO. A PUT here changes the property's data in whatever backend the x-hotelid
// routes to. The screen says so at the moment of sending rather than in a note somebody read once.
//
// A MODAL ON ONE OPERATION, opened by the Test button on that operation's row. The form is only ever
// about the call you clicked, so it belongs over that row rather than in a panel elsewhere on the page
// that has to be told which call it is describing.
//
// NOTHING IS ASKED FOR TWICE. The gateway and the credential's token are established on the page and
// passed in. The x-hotelid is READ FROM THE PROPERTY'S PROVISIONED BINDINGS — a real issued identifier,
// which is what a consumer would actually present. It used to come from a generated value on the page's
// provisioning block, which meant the test could pass with an identifier no caller had been given.
//
// Props:
//   operation   the call being tested — { id, method, route_path, path, description }
//   token       the live access token from the page's authenticate step, or null
//   gatewayUrl  the API's address
//   hotelId     the property, whose bindings supply the x-hotelid
//   onClose     dismiss

// Query strings that make an operation actually work, pre-filled so Send does something on the first
// press.
//
// WHY NOT A PLACEHOLDER. `limit=10&offset=0` sat in this field as grey placeholder text for months and
// read as a value — three Jira tickets (HIL-79/80/81) were raised on an "empty" search that looked
// filled in. Worse, it is the wrong example: limit and offset are PAGING, and OHIP refuses a profile
// search that carries nothing else with OPERAWS-CRM01315.
//
// A REAL VALUE, EDITABLE. The field is populated on open, the hint says where the value came from, and
// clearing it is one keystroke — so the default teaches what a working call looks like instead of
// asking the reader to guess.
//
// Keyed on `METHOD <route><operation>`. The authoritative rule about what a backend requires lives in
// that backend's INTEGRATION (for OHIP, `missingCriteria` in lib/query.js); this is only a starting
// point for a human, so an operation with no entry simply opens empty.
const DEFAULT_QUERY = {
    'GET /crm/v1/profiles': {
        value: 'profileName=Smith&limit=10',
        why: 'OHIP will not list every profile — a search needs at least one criterion '
            + '(profileName, givenName, keyword, city, profileIds, membership …). '
            + 'limit and offset are paging and do not count.',
    },
    'GET /rsv/v1/hotels/{hotelId}/reservations': {
        value: 'arrivalStartDate=2026-01-01&arrivalEndDate=2026-12-31&limit=10',
        why: 'A reservation search needs a date range or another criterion; limit alone returns nothing useful.',
    },
    'GET /blk/v1/blocks': {
        value: 'limit=10',
        why: 'The multi-hotel block search is scoped by the x-hotelid binding, so paging alone is enough.',
    },
    'GET /hsk/v1/hotels/{hotelId}/housekeepingOverview': {
        value: 'fetchInstructions=Summary',
        why: 'Without a fetchInstructions the overview comes back with no room detail.',
    },
};

const WRITE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
const BODY_METHODS = ['POST', 'PUT', 'PATCH'];

// `/hotels/{hotelId}/reservations/{reservationId}` -> ['hotelId', 'reservationId']
const paramsOf = (path) => [...String(path || '').matchAll(/\{([^}]+)\}/g)].map(m => m[1]);

// The path parameter that names the property. In the Universal API it is the SAME routing identifier the
// caller sends as x-hotelid — not the PMS property code — so it is known the moment the binding is, and
// making someone paste a 45-character uuid they can see two lines above is a typo waiting to happen.
const isHotelParam = (name) => /^hotel_?id$/i.test(String(name || ''));

// What a failed call actually means, in words, above the raw JSON.
//
// A backend error arrives as the integration's mapped shape — { statusCode, type, title, detail, code }
// for OHIP, where `code` is the only field stable enough to act on. Reading that out of a pretty-printed
// blob is work the screen can do: the title says what happened, the code is what a search or a ticket is
// keyed on, and OPERAWS-CRM01315 in particular has a fix the reader can apply in the field above.
//
// Advice is keyed on the CODE, never on the prose — OHIP rewords its messages between releases.
const ERROR_ADVICE = {
    'OPERAWS-CRM01315': 'Add a search criterion to the query string above — profileName, givenName, '
        + 'keyword, city, postalCode, profileIds or membership. limit and offset are paging and do not '
        + 'count as criteria.',
    'OPERAWS-GEN01244': 'The body names a property this credential is not authorised for. Check the '
        + 'x-hotelid binding and the property code it resolves to.',
    'OPERAWS-DVM00003': 'DVM headers were sent to a property whose OHIP interface has no Data Value '
        + 'Mapping configured. Turn USE_EXTERNAL_DVM off on the credential.',
};

const explainFailure = (result) => {
    if (!result || result.status < 400) return null;
    let body = result.body;
    if (typeof body === 'string') {
        try { body = JSON.parse(body); } catch (e) { return null; }
    }
    if (!body || typeof body !== 'object') return null;
    const code = body.code || body['o:errorCode'] || null;
    const title = body.title || body.error || null;
    const detail = body.detail !== title ? body.detail : null;
    if (!code && !title && !detail) return null;
    return { code, title, detail, advice: code ? ERROR_ADVICE[code] : null };
};

const fill = (path, values) =>
    String(path || '').replace(/\{([^}]+)\}/g, (whole, name) => (values[name]
        ? encodeURIComponent(values[name])
        : whole));

export default function UapiConsole({
    operation, token, gatewayUrl, hotelId, credentialId, onAuthenticate, onClose,
    // THE CREDENTIALS THIS DIALOG MAY CALL AS. Given a list, it asks the question itself instead of
    // sending the reader back to the page to answer it — composing a call and saying who it is made as
    // are the same act, and splitting them across two screens is what made Test refuse to open at all.
    // Empty (the default) keeps the old behaviour: whatever the caller passed as `credentialId`.
    credentials = [],
    // HANDED IN, OR FETCHED. The Legacy screen knows a property and lets this resolve the bindings; the
    // vendor screen knows a CREDENTIAL and already holds them — and its viewer may be a partner admin,
    // for whom the admin-only access endpoint is a 403. Passing them in is the same dialog reading the
    // same rows from a caller that has them, not a second console.
    bindings: providedBindings = null,
    // THE SUPERADMIN EXTRAS. The gateway trace and the Jira button are /admin/legacy-uapi/* routes. A
    // partner admin testing their own vendor is entitled to the CALL and not to those, and a button
    // that 403s is worse than one that is not there.
    canTrace = true,
    canRaiseIssue = true,
    // WHY THERE IS NOWHERE TO SEND IT. "This property has no backend system bound" is the right
    // sentence on a screen about a property and the wrong one on a screen about a vendor, where the
    // binding is issued to the CREDENTIAL. The caller says what its own empty list means.
    emptyBindingsMessage = null,
}) {
    // WHO THE CALL IS MADE AS, held here. Seeded from the page's choice so the common path is unchanged,
    // and re-seeded if that changes under an open dialog.
    const [activeCredentialId, setActiveCredentialId] = useState(credentialId || '');
    useEffect(() => { setActiveCredentialId(credentialId || ''); }, [credentialId]);

    // A TOKEN BELONGS TO ONE CREDENTIAL. The page hands down the token it minted for ITS choice; the
    // moment this dialog picks a different one, that token is somebody else's and must not be used or
    // counted as "already authenticated" — otherwise switching credential would send the previous
    // caller's bearer and the trace would name a caller the reader did not choose.
    const usableToken = token && String(token.credentialId) === String(activeCredentialId) ? token : null;

    const [bindings, setBindings] = useState(providedBindings || []);
    const [loadingBindings, setLoadingBindings] = useState(!providedBindings);
    const [xHotelId, setXHotelId] = useState('');
    const [pathValues, setPathValues] = useState({});
    const [query, setQuery] = useState('');
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    // The gateway's own account of what it did with the request, by transaction id.
    const [trace, setTrace] = useState(null);
    // The id the gateway minted for the last request. `trace` holds that request's STEPS, so the id
    // has to be kept separately — it is what a trace query and a ticket are both keyed on, and it
    // exists even when no step was ever written.
    const [txId, setTxId] = useState(null);
    const [traceState, setTraceState] = useState('idle'); // idle | loading | ready | none | error
    const [traceError, setTraceError] = useState(null);

    // Raise a Jira from what just happened. Same shape as the OHIP simulator's button — a footer
    // action, a ConfirmModal for the note, and the button turning into a link to the ticket — because
    // that is the one people already know and a second arrangement of the same three things would be a
    // second thing to learn.
    //
    // NO "is this correct?" TICK here. In the simulator the body has been EDITED and the author is
    // saying whether the edit is the fix; here the request and the response are simply what happened,
    // and there is nothing to confirm.
    const [jiraOpen, setJiraOpen] = useState(false);
    const [jiraNote, setJiraNote] = useState('');
    const [jiraCreating, setJiraCreating] = useState(false);
    const [jiraTicket, setJiraTicket] = useState(null);   // { key, url }
    const [jiraError, setJiraError] = useState(null);
    // WHAT THIS CALLER WAS NOT ALLOWED TO SEE. The gateway redacts the backend's answer on the way out
    // and records both documents on the `responded` trace step, so the difference is already on the
    // page — buried at the bottom, inside the right step, behind its Diff button. This surfaces it
    // where the response is, which is where the question gets asked.
    const [redactionOpen, setRedactionOpen] = useState(false);

    // AUTHENTICATE FOR THEM, RATHER THAN TELLING THEM TO.
    //
    // This used to open with "Authenticate above first — a request needs the token that step mints",
    // which is an instruction to go and press a button on the page behind the dialog and come back. The
    // dialog already knows the credential and already has the function that mints a token; there is
    // nothing in that step for a person to decide. It runs on open, and again before a send if the
    // token has lapsed, so the only time anyone reads about authentication is when it FAILED — which is
    // a real finding about the credential rather than a chore.
    const [authing, setAuthing] = useState(false);
    const [authFailed, setAuthFailed] = useState(null);
    // The in-flight attempt, so the open-time mint and a Send that lands during it are the same request.
    // Without this, pressing Send while the banner is up starts a second exchange with the same
    // credential and whichever finishes last wins — wasteful, and two tokens where the page shows one.
    const authInFlight = useRef(null);


    // A new call is a new request, not the previous one with a different path.
    useEffect(() => {
        setPathValues({});
        // Pre-filled, not blank: see DEFAULT_QUERY. An operation with no entry opens empty as before.
        setQuery(DEFAULT_QUERY[`${String(operation?.method || '').toUpperCase()} `
            + `${operation?.route_path || ''}${operation?.path || ''}`]?.value || '');
        setBody('');
        setResult(null);
        setError(null);
        setTrace(null);
        setTraceState('idle');
    }, [operation?.id]);

    // Fill the hotel parameter from the resolved binding. Runs after the reset above and after the
    // bindings load — whichever lands last — and only fills what is still empty, so a value typed by
    // hand is never overwritten.
    useEffect(() => {
        if (!xHotelId || !operation) return;
        const hotelParams = paramsOf(`${operation.route_path || ''}${operation.path || ''}`).filter(isHotelParam);
        if (!hotelParams.length) return;
        setPathValues(prev => {
            const next = { ...prev };
            let changed = false;
            for (const name of hotelParams) {
                if (!next[name]) { next[name] = xHotelId; changed = true; }
            }
            return changed ? next : prev;
        });
    }, [xHotelId, operation]);

    const loadBindings = useCallback(async () => {
        // Already in hand — nothing to ask for. The caller's list is authoritative; re-reading it here
        // would be a second answer to a question already settled, on a route the caller may not have.
        if (providedBindings) {
            setBindings(providedBindings);
            if (providedBindings.length === 1) setXHotelId(providedBindings[0].uapi_hotel_id || '');
            setLoadingBindings(false);
            return;
        }
        if (!hotelId) { setLoadingBindings(false); return; }
        try {
            const res = await legacyUapiService.listAccess(hotelId);
            const list = (res.data || res).access || [];
            setBindings(list);
            // One binding is the common case, and choosing it removes a step with exactly one answer.
            if (list.length === 1) setXHotelId(list[0].uapi_hotel_id || '');
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Could not load this property’s backend systems');
        } finally {
            setLoadingBindings(false);
        }
    }, [hotelId, providedBindings]);

    useEffect(() => { loadBindings(); }, [loadBindings]);

    // Escape closes, as any modal should.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !sending) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, sending]);

    // More than one property among the bindings — see the picker below.
    const multiHotel = new Set(bindings.map(b => String(b.hotel_id))).size > 1;
    const baseUrl = gatewayUrl || '';
    const fullPath = operation ? `${operation.route_path || ''}${operation.path || ''}` : '';
    // Recomputed with the result; cheap, and it keeps the render free of parsing.
    const failure = explainFailure(result);
    // WHO AUTHENTICATED. The dispatcher stamps client_id / credential_id / credential_name on every
    // trace step from the authorizer's context, so any step answers it — and because the gateway only
    // traces a request that got past the authorizer, the presence of a credential here IS the proof
    // that the token was accepted and as whom.
    const caller = (trace || []).find(s2 => s2.credential_id || s2.client_id) || null;
    const queryDefault = operation
        ? DEFAULT_QUERY[`${String(operation.method).toUpperCase()} ${fullPath}`]
        : null;
    const needed = paramsOf(fullPath);
    // The outbound redaction, from the trace this dialog already loads. `responded` is the step where
    // it happens — the dispatcher applies the rules last, so nothing added afterwards can bypass them —
    // and it records the backend's answer alongside what the caller was given.
    const respondedStep = (trace || []).find(st => st.step === 'responded') || null;
    const redactionRules = respondedStep ? Number(respondedStep.rule_count || 0) : 0;
    // Both bodies, and a genuine difference between them. A set can match and still change nothing (no
    // rule touched a field this response carries), and offering a diff of two identical documents
    // invites the reader to hunt for a change that is not there.
    // `redacted_count` is authoritative where the step recorded it. Comparing the two bodies is the
    // fallback for traces written before the dispatcher reported strikes, and it is only ever a
    // heuristic: two identical documents prove nothing about whether a rule ran.
    const redactionChanged = respondedStep && respondedStep.redacted_count != null
        ? Number(respondedStep.redacted_count) > 0
        : !!(respondedStep && respondedStep.body_before && respondedStep.body_after
            && respondedStep.body_before !== respondedStep.body_after);
    const redactedFields = respondedStep ? [].concat(respondedStep.redacted_fields || []) : [];
    const redactionPaths = respondedStep ? [].concat(respondedStep.redaction_paths || []) : [];

    // On open, and whenever the credential changes under it. Not when the token merely expires: a
    // dialog silently re-minting in the background is harder to reason about than one that does it at
    // the moment it needs to, which `send` handles.
    useEffect(() => {
        let cancelled = false;
        if (usableToken || !activeCredentialId || !onAuthenticate) return undefined;
        setAuthing(true);
        setAuthFailed(null);
        const attempt = Promise.resolve(onAuthenticate(activeCredentialId));
        authInFlight.current = attempt;
        attempt
            .then((t) => { if (!cancelled && !t) setAuthFailed('Could not get a token for this credential.'); })
            .catch((e) => { if (!cancelled) setAuthFailed(e?.message || 'Could not get a token for this credential.'); })
            .finally(() => {
                if (authInFlight.current === attempt) authInFlight.current = null;
                if (!cancelled) setAuthing(false);
            });
        return () => { cancelled = true; };
        // `token` is read here but deliberately NOT a dependency: it changes the instant this succeeds,
        // and re-running would authenticate again immediately. The early return covers the only case
        // that matters, which is arriving with a token already in hand.
    }, [activeCredentialId, onAuthenticate, usableToken]);

    const resolvedPath = fill(fullPath, pathValues);
    const missing = needed.filter(n => !pathValues[n]);
    const url = `${String(baseUrl).replace(/\/+$/, '')}${resolvedPath}${query ? `?${query.replace(/^\?/, '')}` : ''}`;

    const isWrite = operation && WRITE_METHODS.includes(String(operation.method).toUpperCase());
    const takesBody = operation && BODY_METHODS.includes(String(operation.method).toUpperCase());

    // A TOKEN IS NO LONGER A PRECONDITION. It is obtained on open and, failing that, at the moment of
    // sending, so requiring one here would disable Send for the second or two the mint takes and leave
    // the reader with a dead button and no explanation. What Send genuinely needs is a credential to
    // authenticate WITH, the gateway, the property and the path's parameters.
    // NOT GATED ON `authing`. Disabling Send while the token is minted is the dead button this was
    // supposed to remove — pressing it mid-mint is fine, because `send` waits for the SAME attempt
    // rather than starting a second one.
    const ready = (usableToken || activeCredentialId) && baseUrl && xHotelId && operation && !missing.length;

    // The trace is written by the gateway as the request is served, but OpenSearch does not make a doc
    // searchable the instant it is written (the index refreshes on its own interval). So this retries a
    // few times rather than reporting "no trace" for a request that was traced half a second ago.
    const loadTrace = async (transactionId) => {
        setTraceState('loading');
        setTraceError(null);
        for (let attempt = 0; attempt < 5; attempt += 1) {
            try {
                const res = await legacyUapiService.getTrace(transactionId);
                const d = res.data || res;
                if (d.steps && d.steps.length) {
                    setTrace(d.steps);
                    setTraceState('ready');
                    return;
                }
            } catch (e) {
                // NOT 'none'. A lookup that failed and a request that was never traced are different
                // facts with different fixes, and collapsing them into one sentence sent us looking at
                // the gateway when the trace ENDPOINT was the thing not deployed.
                setTraceError(apiErrorMessage(e, 'the trace lookup failed'));
                setTraceState('error');
                return;
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise(r => setTimeout(r, 700));
        }
        setTraceState('none');
    };

    const createJira = async () => {
        if (jiraCreating) return;
        setJiraError(null);
        try {
            setJiraCreating(true);
            const binding = bindings.find(b => b.uapi_hotel_id === xHotelId) || {};
            // Deliberately NOT sent: the token. It is on this page and it is a live credential; the
            // server builds the curl with a placeholder instead.
            const res = await legacyUapiService.createCallJira({
                method: String(operation.method).toUpperCase(),
                path: resolvedPath,
                url,
                query: query.replace(/^\?/, '') || null,
                requestBody: takesBody && body.trim() ? body : null,
                status: result ? result.status : null,
                durationMs: result ? result.ms : null,
                responseBody: result ? result.body : null,
                transactionId: txId,
                xHotelId,
                systemName: binding.system_name || null,
                propertyCode: binding.hotelid_override || null,
                credentialId: binding.credential_id || null,
                credentialName: binding.credential_name || null,
                hotelId: binding.hotel_id || hotelId || null,
                hotelName: binding.hotelname || null,
                routePath: operation.route_path || null,
                operationPath: operation.path || null,
                pageUrl: window.location.href,
                note: jiraNote.trim() || undefined,
            });
            const data = res?.data || res;
            setJiraTicket({ key: data.key, url: data.url });
            setJiraOpen(false);
        } catch (e) {
            setJiraError(e.response?.data?.error?.message || e.response?.data?.message
                || e.message || 'Failed to create the Jira ticket');
        } finally {
            setJiraCreating(false);
        }
    };

    const send = async () => {
        setSending(true);
        setResult(null);
        setError(null);
        setTrace(null);
        setTxId(null);
        setTraceState('idle');
        // A ticket belongs to ONE request. Leaving the previous one on screen would offer a link that
        // describes a call the reader can no longer see.
        setJiraTicket(null);
        setJiraError(null);
        const started = performance.now();
        try {
            let parsedBody;
            if (takesBody && body.trim()) {
                try {
                    parsedBody = JSON.stringify(JSON.parse(body));
                } catch (e) {
                    // Caught here rather than sent: a malformed body reaches the gateway as a 400 that
                    // says nothing about which character was wrong.
                    setError(`The body is not valid JSON — ${e.message}`);
                    setSending(false);
                    return;
                }
            }

            // The token may have lapsed while the dialog sat open, or the open-time attempt may still
            // be in flight. Either way this is the moment it is needed, so it is obtained here rather
            // than refused with a message about a step somewhere else.
            let auth = usableToken;
            if (!auth && (authInFlight.current || onAuthenticate)) {
                // Join the attempt already running if there is one; otherwise start our own, which is
                // the case where the token lapsed while the dialog sat open.
                if (authInFlight.current) {
                    auth = await authInFlight.current;
                } else {
                    setAuthing(true);
                    const attempt = Promise.resolve(onAuthenticate(activeCredentialId));
                    authInFlight.current = attempt;
                    try { auth = await attempt; } finally {
                        if (authInFlight.current === attempt) authInFlight.current = null;
                        setAuthing(false);
                    }
                }
            }
            if (!auth) {
                setError('Could not authenticate with this credential, so the request was not sent.');
                setSending(false);
                return;
            }

            const res = await fetch(url, {
                method: String(operation.method).toUpperCase(),
                headers: {
                    Authorization: `${auth.tokenType || 'Bearer'} ${auth.token}`,
                    'x-hotelid': xHotelId,
                    ...(parsedBody ? { 'Content-Type': 'application/json' } : {}),
                },
                ...(parsedBody ? { body: parsedBody } : {}),
            });

            const text = await res.text();
            let pretty = text;
            try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch (e) { /* not JSON */ }

            const headerMap = Object.fromEntries([...res.headers.entries()]);
            setResult({
                status: res.status,
                statusText: res.statusText,
                ms: Math.round(performance.now() - started),
                headers: headerMap,
                body: pretty,
                empty: !text,
            });

            // The id comes back on the header for every response, and in the body for the ones the
            // gateway itself produced. The header is the reliable one.
            let txId = headerMap['x-transaction-id'] || null;
            if (!txId) {
                try { txId = JSON.parse(text).transactionId || null; } catch (e) { /* not ours */ }
            }
            setTxId(txId);
            if (txId && canTrace) loadTrace(txId);
        } catch (e) {
            // A browser fetch failure is almost always CORS or an unreachable host, and the console
            // message ("Failed to fetch") says neither. Both are named because both are fixable.
            setError(`${e.message}. If the gateway is reachable, this is usually CORS: the API must `
                + 'allow this origin and the x-hotelid header.');
        } finally {
            setSending(false);
        }
    };

    const tokenUrl = (usableToken && usableToken.tokenUrl) || `${String(baseUrl).replace(/\/+$/, '')}/oauth/v1/tokens`;
    // THE TWO STEPS AS A CLIENT RUNS THEM: the login, then the call with the token it returned.
    const curlLogin = [
        `curl -s -X POST '${tokenUrl}' -H 'Content-Type: application/x-www-form-urlencoded'`,
        "  -d 'grant_type=client_credentials&client_id=<CLIENTID>&client_secret=<CLIENTSECRET>&APPKEY=<APPKEY>&EXTERNALSYSTEM=<EXTERNALSYSTEM>&ENTERPRISEID=<ENTERPRISEID>'",
    ].join(' \\\n');
    const curlCall = operation ? [
        `curl -i -X ${String(operation.method).toUpperCase()} '${url}'`,
        `  -H 'Authorization: ${usableToken ? `${usableToken.tokenType || 'Bearer'} <token>` : '<token>'}'`,
        `  -H 'x-hotelid: ${xHotelId || '<x-hotelid>'}'`,
        ...(takesBody && body.trim() ? ["  -H 'Content-Type: application/json'", `  -d '${body.replace(/\n\s*/g, '')}'`] : []),
    ].join(' \\\n') : '';
    const curl = operation
        ? `# 1. log in as the vendor (the five values are the credential's)\n${curlLogin}\n\n# 2. the call, with the access_token step 1 returned\n${curlCall}`
        : '';

    return (
        <div className="uc-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}>
            <div className="uc-modal" role="dialog" aria-modal="true">
            <div className="uc-head">
                <div>
                    <h2>Test this call</h2>
                    <span className="lu-muted">
                        Two real steps, as a client makes them: first a login at the Universal API&rsquo;s token endpoint
                        as this credential, then the request at the actual endpoint with that token and this
                        property&rsquo;s x-hotelid.
                    </span>
                </div>
                <button type="button" className="uc-close" onClick={onClose} disabled={sending} aria-label="Close">
                    <X size={16} />
                </button>
            </div>

            <>
                    {/* WHAT IS MISSING, said before the button is reached for rather than as a disabled
                        control with no explanation. */}
                    {/* Only ever shown when something is WRONG or in flight. The ordinary case —
                        a credential is chosen and a token is minted for it — says nothing at all,
                        because there was never a decision for the reader to make. */}
                    {/* AS WHOM — asked here, because this is where a call is composed and who it is made
                        as is part of the call. Only when the caller handed down a list; screens that
                        already know the one credential (the vendor console) pass none and see nothing,
                        which is right — a picker with one option is a question with one answer. */}
                    {credentials.length > 0 && (
                        <div className="uc-cred">
                            <label className="acm-label" htmlFor="uc-cred">Call as</label>
                            <select
                                id="uc-cred"
                                className="acm-select"
                                value={activeCredentialId || ''}
                                onChange={(e) => setActiveCredentialId(e.target.value)}
                                disabled={sending}
                            >
                                <option value="">Choose a credential…</option>
                                {credentials.map((c) => (
                                    <option key={c.credential_id} value={c.credential_id}>
                                        {c.credential_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    {authing && (
                        <p className="uc-auth-note">
                            <Lock size={13} /> Authenticating with this credential…
                        </p>
                    )}
                    {!authing && !activeCredentialId && (
                        <p className="uc-warn">
                            <Lock size={13} /> {credentials.length
                                ? 'Choose a credential — a request is made as one.'
                                : 'This property has no Universal API credential yet, so there is nothing to '
                                  + 'call as. Issue one under Authentication and Access.'}
                        </p>
                    )}
                    {!authing && !usableToken && activeCredentialId && authFailed && (
                        <p className="uc-warn">
                            <Lock size={13} /> {authFailed} Check the credential on the Authentication tab.
                        </p>
                    )}
                    {/* STEP ONE, SAID: the login that just happened — where, how it went, how long the token lasts. The
                        token itself stays masked; the exchange is what a reader checking "does this credential work" and
                        "is this the real endpoint" needs to see. */}
                    {usableToken && (
                        <p className="uc-auth-line">
                            <Lock size={13} />
                            <span><strong>1. Authenticated</strong> — POST <code>{usableToken.tokenUrl || `${String(baseUrl).replace(/\/+$/, '')}/oauth/v1/tokens`}</code>
                                {usableToken.status ? ` → ${usableToken.status}` : ''}{usableToken.ms != null ? ` in ${usableToken.ms} ms` : ''}
                                {usableToken.expiresIn ? `, token valid for ${usableToken.expiresIn} s` : ''}</span>
                        </p>
                    )}
                    {usableToken && !loadingBindings && bindings.length === 0 && (
                        <p className="uc-blocked">
                            <AlertCircle size={13} /> {emptyBindingsMessage
                                || 'This property has no backend system bound, so there is no x-hotelid '
                                   + 'to present. Add one under Authentication and Access.'}
                        </p>
                    )}

                    {/* WHICH BACKEND. Only shown when there is a choice — with one binding the answer is
                        already made and a select with a single option is a decision that isn't one. */}
                    {bindings.length > 1 && (
                        <div className="uc-row">
                            <label className="uc-field uc-field-wide">
                                <span>x-hotelid</span>
                                <select
                                    className="form-input"
                                    value={xHotelId}
                                    onChange={e => setXHotelId(e.target.value)}
                                >
                                    <option value="">Choose the backend system…</option>
                                    {bindings.map(b => (
                                        <option key={b.id} value={b.uapi_hotel_id}>
                                            {/* THE PROPERTY, WHEN THERE IS MORE THAN ONE. On a screen
                                                about one property every option names the same hotel and
                                                the system is the only difference; a vendor's bindings
                                                span its whole estate, where the system alone identifies
                                                nothing. */}
                                            {multiHotel && b.hotelname ? `${b.hotelname} — ` : ''}
                                            {b.system_name} — {b.uapi_hotel_id}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    )}
                    {bindings.length === 1 && (
                        <p className="uc-binding">
                            Routing to <strong>{bindings[0].system_name}</strong>
                            {bindings[0].hotelname ? <> at <strong>{bindings[0].hotelname}</strong></> : null}
                            <code className="lu-mono">{bindings[0].uapi_hotel_id}</code>
                        </p>
                    )}
                    {loadingBindings && <div className="uc-loading"><Spinner size="sm" /></div>}

                    {operation && (
                        <div className="uc-chosen uc-chosen-lead">
                            <span className={`lu-method lu-method-${String(operation.method).toLowerCase()}`}>
                                {operation.method}
                            </span>
                            <code className="lu-mono">{fullPath}</code>
                            {operation.description && (
                                <span className="lu-muted">{operation.description}</span>
                            )}
                        </div>
                    )}

                    {/* The path's own parameters, as fields. Typed into the URL they are the single most
                        common source of a 404 that looks like a broken API. */}
                    {needed.length > 0 && (
                        <div className="uc-row uc-params">
                            {needed.map(name => (
                                <label key={name} className="uc-field">
                                    <span>{name}</span>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={pathValues[name] || ''}
                                        onChange={e => setPathValues(p => ({ ...p, [name]: e.target.value }))}
                                    />
                                </label>
                            ))}
                        </div>
                    )}

                    {operation && (
                        <div className="uc-row">
                            <label className="uc-field uc-field-wide">
                                <span>Query string <em>optional</em></span>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={query}
                                    placeholder="name=value&name=value"
                                    onChange={e => setQuery(e.target.value)}
                                />
                                {/* WHY the field was pre-filled. A default nobody can account for is
                                    indistinguishable from something the reader typed and forgot. */}
                                {queryDefault && (
                                    <span className="uc-field-hint">
                                        Pre-filled with a working example — edit or clear it.
                                        {' '}{queryDefault.why}
                                        {query !== queryDefault.value && (
                                            <button
                                                type="button"
                                                className="uc-link-btn"
                                                onClick={() => setQuery(queryDefault.value)}
                                            >
                                                restore
                                            </button>
                                        )}
                                    </span>
                                )}
                            </label>
                        </div>
                    )}

                    {takesBody && (
                        <label className="uc-field uc-field-block">
                            <span>Body <em>JSON</em></span>
                            <textarea
                                className="form-input uc-body"
                                rows={8}
                                value={body}
                                placeholder='{ "reservations": [ … ] }'
                                onChange={e => setBody(e.target.value)}
                            />
                        </label>
                    )}

                    {operation && (
                        <div className="uc-url">
                            <span className={`lu-method lu-method-${String(operation.method).toLowerCase()}`}>
                                {operation.method}
                            </span>
                            <code className="lu-mono">{url}</code>
                            <CopyButton value={curl} label="curl" className="copy-btn-inline" />
                        </div>
                    )}

                    {/* Not a confirmation dialog — this is a testing tool and a prompt on every send
                        becomes a reflex click. A standing warning that the call is real is the honest
                        middle. */}
                    {isWrite && (
                        <p className="uc-write-warn">
                            <AlertCircle size={13} />
                            {String(operation.method).toUpperCase()} is a real write against
                            {' '}{bindings.find(b => b.uapi_hotel_id === xHotelId)?.system_name || 'the backend'}
                            {' '}for this property.
                        </p>
                    )}

                    <div className="uc-actions">
                        <Button variant="primary" onClick={send} disabled={!ready || sending}>
                            <Send className="icon" /> {sending ? 'Sending…' : 'Send'}
                        </Button>

                        {/* BESIDE SEND, NOT INSIDE THE RESULT. This used to live in the response header
                            on the reasoning that a ticket wants the status you have just seen. That was
                            wrong about when people raise one: the call that will not go, the operation
                            that 404s the moment it is opened, the request you want to ask about before
                            firing it at a live property — in every one of those the button was not on
                            screen at all, because there was no response for it to sit in.
                            A result is attached when there is one and left out when there is not; the
                            endpoint requires only the method and the path. */}
                        {canRaiseIssue && (jiraTicket ? (
                            <a
                                className="btn btn-secondary uc-jira-link"
                                href={jiraTicket.url}
                                target="_blank"
                                rel="noreferrer"
                                title="Open the Jira ticket raised from this call"
                            >
                                <ExternalLink size={12} /> {jiraTicket.key}
                            </a>
                        ) : (
                            <Button
                                variant="secondary"
                                onClick={() => { setJiraNote(''); setJiraError(null); setJiraOpen(true); }}
                                disabled={jiraCreating}
                                title={result
                                    ? 'Raise a Jira with this request, this response and the transaction id'
                                    : 'Raise a Jira about this call — send it first to attach the response'}
                            >
                                <Bug size={12} /> {jiraCreating ? 'Creating…' : 'Jira'}
                            </Button>
                        ))}

                        {missing.length > 0 && (
                            <span className="lu-muted">Fill {missing.join(', ')} first.</span>
                        )}
                    </div>

                    {error && (
                        <p className="uc-error"><AlertCircle size={13} /> {error}</p>
                    )}

                    {result && (
                        <div className="uc-result">
                            <div className="uc-result-head">
                                <strong>2. {String(operation.method).toUpperCase()} {url}</strong>
                                <span className={`uc-status uc-status-${String(result.status)[0]}xx`}>
                                    {result.status} {result.statusText}
                                </span>
                                <span className="lu-muted"><Clock size={11} /> {result.ms} ms</span>
                                {/* THE TRANSACTION ID, on every response and not only when the trace
                                    loads. It is the join key for the trace, the Logs tab and a Jira
                                    ticket, so it belongs beside the status rather than inside a panel
                                    that may not have rendered. */}
                                {txId && (
                                    <span className="uc-txid">
                                        <code className="lu-mono">{txId}</code>
                                        <CopyButton value={txId} label="Copy" className="copy-btn-inline" />
                                    </span>
                                )}
                                {/* The gateway's request id is what a CloudWatch search is keyed on, so
                                    it is surfaced rather than left in the headers list. */}
                                {result.headers['apigw-requestid'] && (
                                    <span className="lu-muted">
                                        request {result.headers['apigw-requestid']}
                                    </span>
                                )}
                                <CopyButton value={result.body} label="Copy" className="copy-btn-inline" />

                                {/* WHAT WAS TAKEN OUT OF THIS RESPONSE. On the response header because
                                    that is the document the question is about — "is this everything?"
                                    is asked while reading it, not while scrolling a trace.
                                    Shown only when the trace has arrived and a redaction set actually
                                    matched. Its label says which of the two answers it will give, so a
                                    caller who sees "not redacted" learns that from the button rather
                                    than from opening an empty diff. */}
                                {respondedStep && respondedStep.redaction_set && (
                                    <button
                                        type="button"
                                        className="uc-redaction-btn"
                                        onClick={() => setRedactionOpen(true)}
                                        title={redactionChanged
                                            ? `Compare the backend's answer with what you were given — `
                                              + `set "${respondedStep.redaction_set}"`
                                              + (redactedFields.length ? `, removed ${redactedFields.join(', ')}` : '')
                                            : `Set "${respondedStep.redaction_set}" matched but removed nothing — `
                                              + `open for why`}
                                    >
                                        <EyeOff size={12} />
                                        {redactionChanged ? 'Redaction' : 'Not redacted'}
                                    </button>
                                )}
                            </div>
                            {/* The refusal in words, before the JSON. Rendered only when the body
                                actually carries an error shape, so a success is unaffected. */}
                            {failure && (
                                <div className="uc-failure">
                                    {failure.title && <p className="uc-failure-title">{failure.title}</p>}
                                    {failure.detail && <p className="uc-failure-detail">{failure.detail}</p>}
                                    {failure.code && (
                                        <p className="uc-failure-code">
                                            <span>{failure.code}</span>
                                            <CopyButton value={failure.code} label="Copy" className="copy-btn-inline" />
                                        </p>
                                    )}
                                    {failure.advice && <p className="uc-failure-advice">{failure.advice}</p>}
                                </div>
                            )}
                            {result.empty
                                ? <p className="lu-muted">No body.</p>
                                : <pre className="uc-body-out">{result.body}</pre>}
                        </div>
                    )}

                    {/* WHAT THE GATEWAY DID WITH IT — who authenticated, which modules were granted,
                        which redaction set matched and what it removed, what went to the backend and
                        what came back. From the trace index, on the screen that made the call rather
                        than in a query somebody has to know how to write.

                        RENDERED WHENEVER THERE IS A RESULT, including when there is no trace to show.
                        It used to render only once a transaction id had been found, so a response that
                        did not yield one produced NOTHING — no heading, no explanation, just the body
                        and then the end of the modal. "Where is the logging information" is what that
                        silence produces, and the answer has to be on the screen. */}
                    {result && canTrace && (
                        <div className="uc-trace">
                            <div className="uc-trace-head">
                                <strong>Gateway trace</strong>
                                {traceState === 'loading' && <span className="lu-muted">reading…</span>}
                                {traceState === 'ready' && (
                                    <span className="lu-muted">{trace.length} step(s)</span>
                                )}
                                {trace && trace[0] && (
                                    <code className="lu-mono uc-trace-id">{trace[0].transaction_id}</code>
                                )}
                            </div>

                            {/* Stated once, not per step: it is the same on all of them, and repeating
                                it eight times buries the steps that differ. */}
                            {caller && (
                                <p className="uc-trace-caller">
                                    <strong>Authenticated</strong> as{' '}
                                    {caller.credential_name || `credential ${caller.credential_id}`}
                                    {caller.credential_name && caller.credential_id
                                        ? ` (id ${caller.credential_id})` : ''}
                                    {caller.client_id && <> · client <code>{caller.client_id}</code></>}
                                    {caller.x_hotelid && <> · x-hotelid <code>{caller.x_hotelid}</code></>}
                                </p>
                            )}

                            {/* No id came back, so nothing was ever looked up. Named precisely, because
                                the cause is a missing response header and not a missing trace: the
                                gateway wrote one, the browser just cannot see which. */}
                            {traceState === 'idle' && (
                                <p className="uc-trace-error">
                                    No transaction id came back with this response, so the trace could not
                                    be looked up. The gateway sets <code>x-transaction-id</code> on every
                                    reply, but a browser can only read a response header the API lists in
                                    <code> Access-Control-Expose-Headers</code> — until the gateway is
                                    redeployed with that header, this panel stays empty even though the
                                    request WAS traced.
                                    {' '}Everything below is still recorded: open
                                    {' '}<strong>Universal API &rsaquo; Logs</strong>, which reads traces by
                                    property rather than from a response header.
                                </p>
                            )}

                            {traceState === 'loading' && <div className="uc-loading"><Spinner size="sm" /></div>}

                            {traceState === 'none' && (
                                <p className="lu-muted">
                                    No trace for this request. Traces are kept for 3 days, and a gateway
                                    that has not been redeployed since tracing shipped writes none.
                                </p>
                            )}

                            {traceState === 'error' && (
                                <p className="uc-trace-error">
                                    Could not read the trace: {traceError}. This is the lookup failing,
                                    not the gateway — the request itself may well have been traced.
                                </p>
                            )}

                            {traceState === 'ready' && <UapiTraceSteps steps={trace} />}
                        </div>
                    )}
            </>
            </div>

            {/* ConfirmModal, not window.confirm — STYLE.md. The note is optional here: the request and
                the response are already the evidence, and forcing a sentence out of somebody who has
                nothing to add produces "doesn't work". */}
            {/* The same diff the `responded` step offers in the trace below, opened from the response
                itself. Reuses DiffModal and the trace's own labels rather than restating what the two
                documents are — the distinction between inbound pruning and outbound redaction is
                written down once, in UapiTraceSteps, and both callers read it from there. */}
            {redactionOpen && respondedStep && (
                redactionChanged ? (
                    <DiffModal
                        title={`responded — ${DIFF_TITLE.responded}`}
                        subtitle={`${DIFF_SUBTITLE.responded} Set "${respondedStep.redaction_set}" `
                            + `(${respondedStep.redaction_match === 'grant'
                                ? 'chosen on the grant for this call'
                                : `matched ${respondedStep.redaction_match}`}), ${redactionRules} rule(s).`}
                        leftLabel={labelsFor('responded').before}
                        rightLabel={labelsFor('responded').after}
                        left={prettyBody(respondedStep.body_before)}
                        right={prettyBody(respondedStep.body_after)}
                        onClose={() => setRedactionOpen(false)}
                    />
                ) : (
                    <ConfirmModal
                        title="Nothing was redacted"
                        message={`The redaction set "${respondedStep.redaction_set}" applied to this call `
                            + `(${redactionRules} rule(s)), but nothing was removed — what you are reading `
                            + `is the backend's answer in full. `
                            + (respondedStep.redactable === false
                                ? 'The response is not a JSON document, so path rules had nothing to address.'
                                : redactionPaths.length
                                    ? `No field matched ${redactionPaths.join(', ')}. Check the rule's path `
                                      + `against the shape of the response above — a field inside an array `
                                      + `needs the array in its path.`
                                    : 'No rule matched a field this response carries.')}
                        confirmLabel="Close"
                        onConfirm={() => setRedactionOpen(false)}
                        onCancel={() => setRedactionOpen(false)}
                    />
                )
            )}

            {canRaiseIssue && jiraOpen && (
                <ConfirmModal
                    title="Raise a Jira for this call"
                    message={'The ticket carries the exact request, the response, where it routed and the '
                        + 'transaction id, so the fix can reproduce it. The bearer token is never included.'}
                    confirmLabel={jiraCreating ? 'Creating…' : 'Create ticket'}
                    onConfirm={createJira}
                    onCancel={() => setJiraOpen(false)}
                >
                    <textarea
                        className="cm-textarea"
                        value={jiraNote}
                        onChange={e => { setJiraNote(e.target.value); setJiraError(null); }}
                        placeholder="What were you expecting to happen? (optional)"
                    />
                    {jiraError && <div className="error-banner"><AlertCircle size={13} />{jiraError}</div>}
                </ConfirmModal>
            )}
        </div>
    );
}
